import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Select,
  message,
  Checkbox,
  Card,
  Tooltip,
  Modal,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Box, Container, Typography, Avatar, IconButton } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
// Added Typography and Avatar
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudentData } from "../../store/PersonalBasicInfoSlice";
import EditBasicInformation from "../../components/forms/EditBasicInformation";
import EditAdditionalInformationParent from "../../components/forms/EditAdditionalInformationParent";
import EditAdditionalInformationStudent from "../../components/forms/EditAdditionalInformationStudent";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";
import AccessDenied from '../../hooks/AccessDenied'; // Adjust the import path

// Custom Styles for Ant Design components (using styled components is also possible)
const tableStyle = {
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
};

const headerStyle = {
  backgroundColor: "#f0f2ff", // Light blue-ish background
  fontWeight: "500", // Reduced from bold
  fontSize: "0.9rem", // Reduced font size
  color: "#333",
};

const actionButtonStyle = {
  margin: "0 4px",
  borderRadius: "5px",
};

const PersonalBasicInfo = () => {
  const [personalData, setPersonalData] = useState([]);

    const dispatch = useDispatch();

  const studentDetail = useSelector((state) => state.studentdata.studentDetail);
  const parentDetails = useSelector((state) => state.studentdata.parentDetails);
  const loading = useSelector((state) => state.studentdata.loading);
  const error = useSelector((state) => state.studentdata.error);

  useTokenExpiryCheck();

  useEffect(() => {
    dispatch(fetchStudentData());
  }, [dispatch]);

  const [isBasicModalOpen, setIsBasicModalOpen] = useState(false);
  const [isParentModalOpen, setParentIsModalOpen] = useState(false);
  const [isStudentModalOpen, setStudentIsModalOpen] = useState(false);
  const [isAdditionalStudentModalOpen, setIsAdditionalStudentModalOpen] =
    useState(false);
  const [editingBasicInfo, setEditingBasicInfo] = useState(null);
  const [editingStudentInfo, setEditingStudentInfo] = useState(null);
  const [editingParentInfo, setEditingParentInfo] = useState(null);
   const [isParentEditModalVisible, setIsParentEditModalVisible] =
    useState(false);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [isStudentEditModalVisible, setIsStudentEditModalVisible] =
    useState(false);

    const storedUser = localStorage.getItem("userInfo");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    if (!userData) {
      throw new Error("No user data or token found");
    }
    const isStudent = userData?.roles?.includes("Student") ? true : false;

    
  useEffect(() => {
    const keys = [
      "id",
      "firstName",
      "surName",
      "lastName",
      "email",
      "phoneNumber",
      "parentType",
      "basicInfoId",
    ];

    const createCombinedObject = (source) => {
      return keys.reduce((acc, key) => {
        acc[key] = source[key] !== undefined ? source[key] : null;
        return acc;
      }, {});
    };

    const staticPersonalData = [
      { ...createCombinedObject(studentDetail), relation: "Student" },
      ...parentDetails.map((parent) => ({
        ...createCombinedObject(parent),
        relation: "Parent",
      })),
    ];

    const formattedData = staticPersonalData.map((person) => ({
      key: person.id,
      id: person.id,
      parentId: person.parentId,
      basicInfoId: person.basicInfoId,
      fullName: `${person.firstName} ${person.surName || ""} ${
        person.lastName
      }`.trim(),
      relation: person.relation,
      email: person.email,
      phoneNumber: person.phoneNumber,
      parentType: person.parentType,
    }));
    setPersonalData(formattedData);
  }, [studentDetail, parentDetails]);

  if (!studentDetail || !parentDetails) {
    return <div>Loading...</div>;
  }
  if (loading === "loading") {
    return <div>Loading student data...</div>;
  }

  if (loading === "failed") {
    return <div>Error loading data: {error}</div>;
  }

  const handleEditBasicInfo = (record) => {
    if (record.relation === "Parent") {
      const parent = parentDetails.find((p) => p.id === record.id);
      if (parent) {
        const parentData = {
          ...parent,
          id: parent.id,
          firstName: parent.firstName,
          surName: parent.surName,
          lastName: parent.lastName,
          email: parent.email,
          phoneNumber: parent.phoneNumber,
          dob: parent.dob,
          gender: parent.gender,
          nationality: parent.nationality,
          birthCountry: parent.birthCountry,
          idInformationCountry: parent.idInformationCountry,
          idNumber: parent.idNumber,
          idCopyFile: parent.idCopyFile,
          idCopyFileName: parent.idCopyFileName,
          idCopyFileContentType: parent.idCopyFileContentType,
          photo: parent.photo,
          photoFileName: parent.photoFileName,
          photoFileContentType: parent.photoFileContentType,
          relation: "Parent",
        };
        setEditingBasicInfo(parentData);
      }
    } else {
      setEditingBasicInfo({
        ...studentDetail,
        id: studentDetail.studentId,
        relation: "Student",
      });
    }

    setIsBasicModalOpen(true);
    setParentIsModalOpen(false);
    setStudentIsModalOpen(false);
    setIsAdditionalStudentModalOpen(false);
  };

  const handleEditAdditionalInfo = async (record) => {
    if (record.relation === "Parent") {
      const parentId = record.id;
      setSelectedParentId(parentId);
      setIsParentEditModalVisible(true);
    } else {
      const studentId = studentDetail.studentId || studentDetail.id;
      setIsStudentEditModalVisible(true);
    }
  };

  const handleModalCancel = () => {
    setIsBasicModalOpen(false);
    setParentIsModalOpen(false);
    setStudentIsModalOpen(false);
    setIsAdditionalStudentModalOpen(false);
    setEditingBasicInfo(null);
    setEditingParentInfo(null);
    setEditingStudentInfo(null);
  };

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar>{text.charAt(0)}</Avatar>
          <Typography variant="body2" style={{ marginLeft: "8px" }}>
            {text}
          </Typography>
        </Box>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Relation",
      dataIndex: "relation",
      key: "relation",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditBasicInfo(record)}
            size="small"
          >
            Edit Basic Info
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditAdditionalInfo(record)}
            size="small"
          >
            Edit Additional Info
          </Button>
        </Space>
      ),
    },
  ];
  const customHeader = columns.map((col) => ({
    ...col,
    onHeaderCell: () => ({ style: headerStyle }),
  }));
  // if (!isStudent) {
  //   return <AccessDenied />;
  // }  

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          p: { xs: 1, md: 2 },
          mt: 2,
          borderRadius: "12px",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography
            color="primary"
            variant="h5"
            component="h3"
            sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
          >
            <PersonIcon color="primary" style={{ marginRight: 8 }} />
            Personal Information
          </Typography>
        </Box>
        {/* Add any additional components here, like a search bar or filters */}
        <Table
          columns={customHeader}
          dataSource={personalData}
          loading={loading}
          style={tableStyle}
        />
        {isBasicModalOpen && (
          <EditBasicInformation
            visible={isBasicModalOpen}
            onCancel={() => {
              handleModalCancel();
              dispatch(fetchStudentData()); // Refresh the student and parent data
            }}
            id={editingBasicInfo?.id}
            basicInfoId={editingBasicInfo?.basicInfoId}
            editingBasicInfo={editingBasicInfo}
            relation={editingBasicInfo?.relation?.toLowerCase()}
          />
        )}

        {/* Parent Additional Information Modal */}
        <Modal
          title="Edit Parent Additional Information"
          open={isParentEditModalVisible}
          onCancel={() => {
            setIsParentEditModalVisible(false);
            dispatch(fetchStudentData()); // Refresh the student and parent data
          }}
          footer={null}
          width={1000}
        >
          <EditAdditionalInformationParent
            parentId={selectedParentId}
            onClose={() => {
              setIsParentEditModalVisible(false);
              dispatch(fetchStudentData()); // Refresh the student and parent data
            }}
          />
        </Modal>
        {/* Student Additional Information Modal */}
        <Modal
          title="Edit Student Additional Information"
          open={isStudentEditModalVisible}
          onCancel={() => setIsStudentEditModalVisible(false)}
          footer={null}
          width={1000}
        >
          <EditAdditionalInformationStudent
            studentId={studentDetail?.studentId || studentDetail?.id}
            onClose={() => {
              setIsStudentEditModalVisible(false);
              dispatch(fetchStudentData()); // Refresh the data
            }}
          />
        </Modal>
      </Box>
    </Box>
  );
};

export default PersonalBasicInfo;
